import React, { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Building, Info } from 'lucide-react';
import Modal from '../ui/Modal';
import Input from '../ui/Input';
import Button from '../ui/Button';
import Select from '../ui/Select';
import Checkbox from '../ui/Checkbox';
import { useToast } from '../../contexts/ToastContext';
import { RootState } from '../../store';
import {
  inviteUserToAccountRequest,
  updateUserRequest,
  clearAccountState,
} from '../../store/accounts/accounts.action';
import {
  selectInviteUserLoading,
  selectInviteUserError,
  selectInviteUserSuccess,
  selectUpdateUserLoading,
  selectUpdateUserError,
  selectUpdateUserSuccess,
} from '../../store/accounts/accounts.selector';
import { selectAdminWorkspaces } from '../../store/workspace/workspace.selector';
import { getAdminWorkspacesRequest } from '../../store/workspace/workspace.action';
import { IWorkspace } from '../../types/workspace/workspace.types';
import { User } from '../../types/accounts/accounts.types';

interface InviteUserToAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  user?: User | null;
}

const InviteUserToAccountModal: React.FC<InviteUserToAccountModalProps> = ({
  isOpen,
  onClose,
  user = null,
}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  // Determine if we're in edit mode
  const isEditMode = !!user;

  // Local state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member'>('member');
  const [permissionType, setPermissionType] = useState<'all' | 'custom'>('all');
  const [allWorkspacesRole, setAllWorkspacesRole] = useState<
    'admin' | 'member'
  >('member');
  const [selectedWorkspaces, setSelectedWorkspaces] = useState<{
    [workspaceId: string]: string;
  }>({});

  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [permissionError, setPermissionError] = useState('');

  // Redux state
  const inviteLoading = useSelector((state: RootState) =>
    selectInviteUserLoading(state)
  );
  const inviteError = useSelector((state: RootState) =>
    selectInviteUserError(state)
  );
  const inviteSuccess = useSelector((state: RootState) =>
    selectInviteUserSuccess(state)
  );
  const updateLoading = useSelector((state: RootState) =>
    selectUpdateUserLoading(state)
  );
  const updateError = useSelector((state: RootState) =>
    selectUpdateUserError(state)
  );
  const updateSuccess = useSelector((state: RootState) =>
    selectUpdateUserSuccess(state)
  );
  const workspaces = useSelector((state: RootState) =>
    selectAdminWorkspaces(state)
  );

  // Use appropriate loading/error/success based on mode
  const loading = isEditMode ? updateLoading : inviteLoading;
  const error = isEditMode ? updateError : inviteError;
  const success = isEditMode ? updateSuccess : inviteSuccess;

  // Load admin workspaces when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getAdminWorkspacesRequest({ isAdmin: true }));
    }
  }, [isOpen, dispatch]);

  // Populate form when user data is available (edit mode)
  useEffect(() => {
    if (user && isOpen) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      setRole(
        (user.permissions?.accountRole as 'admin' | 'member') || 'member'
      );
      // Default permissions - in a real app, you'd fetch these from the user object
      setPermissionType((user.permissions?.type as 'all' | 'custom') || 'all');
      setAllWorkspacesRole(
        (user.permissions?.workspaceRole as 'admin' | 'member') || 'member'
      );
      setSelectedWorkspaces(user.permissions?.workspaces || {});
    }
  }, [user, isOpen]);

  // Handle error toast
  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: isEditMode
          ? 'Failed to Update User'
          : 'Failed to Send Invitation',
        message: error,
      });
      dispatch(clearAccountState());
    }
  }, [error, showToast, dispatch, isEditMode]);

  // Email validation
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle workspace checkbox change
  const handleWorkspaceToggle = (workspaceId: string, checked: boolean) => {
    if (checked) {
      setSelectedWorkspaces((prev) => ({
        ...prev,
        [workspaceId]: 'member', // Default role
      }));
    } else {
      setSelectedWorkspaces((prev) => {
        const newState = { ...prev };
        delete newState[workspaceId];
        return newState;
      });
    }
    setPermissionError('');
  };

  // Handle workspace role change
  const handleWorkspaceRoleChange = (workspaceId: string, role: string) => {
    setSelectedWorkspaces((prev) => ({
      ...prev,
      [workspaceId]: role,
    }));
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditMode && !user) return;

    // Clear previous errors
    setFirstNameError('');
    setLastNameError('');
    setEmailError('');
    setPermissionError('');
    dispatch(clearAccountState());

    // Validate fields
    let hasError = false;

    if (!firstName.trim()) {
      setFirstNameError('First name is required');
      hasError = true;
    }

    if (!lastName.trim()) {
      setLastNameError('Last name is required');
      hasError = true;
    }

    if (!email.trim()) {
      setEmailError('Email is required');
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      hasError = true;
    }

    // Validate permissions for member role
    if (role === 'member' && permissionType === 'custom') {
      if (Object.keys(selectedWorkspaces).length === 0) {
        setPermissionError('Please select at least one workspace');
        hasError = true;
      }
    }

    if (hasError) {
      return;
    }

    // Prepare permissions object
    const permissions = {
      type: permissionType,
      ...(permissionType === 'all' && {
        workspaceRole: allWorkspacesRole,
      }),
      ...(permissionType === 'custom' && {
        workspaces: selectedWorkspaces,
      }),
    };

    // Dispatch appropriate action based on mode
    if (isEditMode && user) {
      dispatch(
        updateUserRequest({
          userId: user._id,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: email.trim(),
          role,
          permissions,
        })
      );
    } else {
      dispatch(
        inviteUserToAccountRequest({
          email: email.trim(),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          role,
          permissions,
        })
      );
    }
  };

  // Handle modal close
  const handleClose = useCallback(() => {
    if (!loading) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setRole('member');
      setPermissionType('all');
      setAllWorkspacesRole('member');
      setSelectedWorkspaces({});
      setFirstNameError('');
      setLastNameError('');
      setEmailError('');
      setPermissionError('');
      dispatch(clearAccountState());
      onClose();
    }
  }, [loading, dispatch, onClose]);

  // Handle success toast
  useEffect(() => {
    if (success) {
      showToast({
        type: 'success',
        title: isEditMode
          ? 'User Updated Successfully'
          : 'Invitation Sent Successfully',
        message: isEditMode
          ? `User ${email} has been updated successfully.`
          : `An invitation has been sent to ${email}. They will receive an email with instructions to join your account.`,
      });
      dispatch(clearAccountState());
      handleClose();
    }
  }, [success, email, showToast, dispatch, handleClose, isEditMode]);

  // Reset permission type when role changes
  useEffect(() => {
    if (role === 'admin') {
      setPermissionType('all');
      setAllWorkspacesRole('admin');
      setSelectedWorkspaces({});
    }
  }, [role]);

  const isFormValid =
    firstName.trim() &&
    lastName.trim() &&
    email.trim() &&
    validateEmail(email) &&
    !(
      role === 'member' &&
      permissionType === 'custom' &&
      Object.keys(selectedWorkspaces).length === 0
    );

  if (isEditMode && !user) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? 'Edit Team Member' : 'Team Member Invitation'}
      size="small"
    >
      <div className="space-y-0">
        {/* Form */}
        <form
          id={isEditMode ? 'edit-user-form' : 'invite-user-form'}
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* User Details Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold text-foreground">
                User details
              </h3>
              <div className="group relative">
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-popover text-popover-foreground border border-border text-xs rounded-lg shadow-lg z-10">
                  {isEditMode
                    ? "Update the user's information and permissions."
                    : "Enter the user's full name and email address. They will receive an invitation email."}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                type="text"
                label="Name"
                placeholder="Enter first name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                error={firstNameError}
                required
                disabled={loading || user?.signUpComplete}
                className="w-full"
                size="sm"
              />
              <Input
                type="text"
                label="Last name"
                placeholder="Enter last name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                error={lastNameError}
                required
                disabled={loading || user?.signUpComplete}
                className="w-full"
                size="sm"
              />
            </div>
            <Input
              type="email"
              label="Email address"
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={emailError}
              required
              disabled={loading || user?.signUpComplete}
              className="w-full"
              size="sm"
            />
          </div>

          {/* Account Level Permissions Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold text-foreground">
                Account level permissions
              </h3>
              <div className="group relative">
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-56 p-2 bg-popover text-popover-foreground border border-border text-xs rounded-lg shadow-lg z-10">
                  Admin: Full access to all account features. Member: Limited
                  access based on workspace permissions.
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Select
                label="Role"
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'member')}
                options={[
                  { value: 'admin', label: 'Admin' },
                  { value: 'member', label: 'Member' },
                ]}
                required
                disabled={loading}
                className="w-full text-xs"
                size="xs"
              />
            </div>
          </div>

          {/* Workspace Level Permissions Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-semibold text-foreground">
                Workspace level permissions
              </h3>
              <div className="group relative">
                <Info className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-64 p-2 bg-popover text-popover-foreground border border-border text-xs rounded-lg shadow-lg z-10">
                  Choose access level: All workspaces or select specific ones.
                  Each workspace can have admin or member role.
                </div>
              </div>
            </div>

            {role === 'admin' ? (
              <div className="bg-info-background border border-info/30 rounded-lg p-2.5">
                <p className="text-xs text-info">
                  Access to all workspaces as admin
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* All Option */}
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="permissionType"
                      value="all"
                      checked={permissionType === 'all'}
                      onChange={(e) => {
                        setPermissionType('all');
                        setSelectedWorkspaces({});
                        setPermissionError('');
                      }}
                      disabled={loading}
                      className="w-3.5 h-3.5 accent-primary border-border focus:outline-none focus:ring-0 cursor-pointer"
                    />
                    <span className="ml-2 text-xs font-medium text-foreground">
                      All
                    </span>
                  </label>
                  {permissionType === 'all' && (
                    <div className="ml-6 mt-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          Access to all workspaces as
                        </span>
                        <Select
                          label=""
                          value={allWorkspacesRole}
                          onChange={(e) =>
                            setAllWorkspacesRole(
                              e.target.value as 'admin' | 'member'
                            )
                          }
                          options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'member', label: 'Member' },
                          ]}
                          disabled={loading}
                          className="w-32 text-xs"
                          size="xs"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Custom Option */}
                <div className="space-y-2">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="permissionType"
                      value="custom"
                      checked={permissionType === 'custom'}
                      onChange={(e) => {
                        setPermissionType('custom');
                        setPermissionError('');
                      }}
                      disabled={loading}
                      className="w-3.5 h-3.5 accent-primary border-border focus:outline-none focus:ring-0 cursor-pointer"
                    />
                    <span className="ml-2 text-xs font-medium text-foreground">
                      Custom
                    </span>
                  </label>
                  {permissionType === 'custom' && (
                    <div className="ml-6 mt-1.5 space-y-2">
                      <p className="text-xs text-muted-foreground">
                        Select workspaces and assign roles for each.
                      </p>
                      {permissionError && (
                        <p className="text-xs text-destructive">
                          {permissionError}
                        </p>
                      )}
                      <div className="border border-border rounded-lg p-2.5 bg-muted/30 space-y-2 max-h-48 overflow-y-auto scrollbar-thin">
                        {workspaces.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-3">
                            No workspaces available
                          </p>
                        ) : (
                          workspaces.map((workspace: IWorkspace) => (
                            <div
                              key={workspace._id}
                              className="flex items-center justify-between "
                            >
                              <div className="flex items-center space-x-2 flex-1 min-w-0">
                                <Checkbox
                                  checked={
                                    !!selectedWorkspaces[workspace._id || '']
                                  }
                                  onChange={(e) =>
                                    handleWorkspaceToggle(
                                      workspace._id || '',
                                      e.target.checked
                                    )
                                  }
                                  disabled={loading}
                                />
                                <div className="flex items-center space-x-1.5 flex-1 min-w-0">
                                  <Building className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                  <span className="text-xs font-medium text-foreground truncate">
                                    {workspace.name}
                                  </span>
                                </div>
                              </div>
                              {selectedWorkspaces[workspace._id || ''] && (
                                <Select
                                  value={
                                    selectedWorkspaces[workspace._id || ''] ||
                                    'member'
                                  }
                                  onChange={(e) =>
                                    handleWorkspaceRoleChange(
                                      workspace._id || '',
                                      e.target.value
                                    )
                                  }
                                  options={[
                                    { value: 'admin', label: 'Admin' },
                                    { value: 'member', label: 'Member' },
                                  ]}
                                  size="xs"
                                  disabled={loading}
                                  className="w-28 ml-2 text-xs"
                                />
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-border mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              size="small"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading || !isFormValid}
              size="small"
            >
              {loading ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin mr-1.5" />
                  {isEditMode ? 'Updating...' : 'Saving...'}
                </>
              ) : isEditMode ? (
                'Update'
              ) : (
                'Save'
              )}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default InviteUserToAccountModal;
